import { useQuery as defaultUseQuery } from '@apollo/react-hooks';
import { useNextPlan, usePlanStacks } from '@zapier/upsells/src/hooks';

import {
  Account,
  ApolloInjection,
  PlanType,
  TrialType,
  UpgradeReason,
} from '@zapier/upsells/src/types';

import { getNextPlanTypeForUpgradeReason } from '@zapier/upsells/src/utils';

type Props = {
  account: Account | undefined;
  activeTrialType: TrialType | undefined;
  upgradeReason: UpgradeReason;
} & ApolloInjection;

export const usePlanForUpgradeReason = ({
  account,
  activeTrialType,
  upgradeReason,
  useQuery = defaultUseQuery,
}: Props) => {
  const {
    error: planStacksError,
    isLoading: isLoadingPlanStacks,
    planStacks,
  } = usePlanStacks({ useQuery });

  let planType: PlanType | undefined;

  if (account?.plan && planStacks && planStacks.length > 0) {
    planType = getNextPlanTypeForUpgradeReason({
      activeTrialType,
      currentPlan: account?.plan,
      planStacks,
      upgradeReason,
    });
  }

  const {
    error: planError,
    isLoading: isLoadingPlan,
    nextPlan: plan,
  } = useNextPlan({
    accountId: account?.id,
    interval: account?.plan?.interval,
    skip: !account || !planType || isLoadingPlanStacks,
    type: planType,
    useQuery,
  });

  const isLoading = !account || isLoadingPlan || isLoadingPlanStacks;

  return {
    error:
      (!planType && !isLoading) || planError || planStacksError
        ? 'An error has occurred. Please try again.'
        : undefined,
    isLoading,
    plan,
  };
};
